import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import type { CrmConnectorDriverMetadataField } from '../../constants/crmConnector';

interface CrmConnectorMetadataDialogProps {
  open: boolean;
  accountName: string;
  field: CrmConnectorDriverMetadataField | null;
  currentValue?: string | number;
  onClose: () => void;
  onSave: (value: string | number) => Promise<void>;
}

export default function CrmConnectorMetadataDialog({
  open,
  accountName,
  field,
  currentValue,
  onClose,
  onSave,
}: CrmConnectorMetadataDialogProps) {
  const [draftValue, setDraftValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftValue(currentValue === undefined ? '' : String(currentValue));
      setError(null);
      setSubmitting(false);
    }
  }, [open, currentValue]);

  const isInteger = field?.type === 'integer';
  const isInvalid = isInteger ? !/^[1-9]\d*$/.test(draftValue.trim()) : draftValue.trim() === '';

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onSave(isInteger ? Number(draftValue.trim()) : draftValue.trim());
      onClose();
    } catch (requestError: unknown) {
      setError(requestError instanceof Error && requestError.message ? requestError.message : `Failed to save ${field?.label ?? 'the setting'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle>{field?.label}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          autoFocus
          type={isInteger ? 'number' : 'text'}
          label={field?.label}
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          disabled={submitting}
          error={isInvalid}
          helperText={`${field?.helperText ?? ''} (${accountName})`}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || isInvalid}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
